import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format, isValid, parseISO } from 'date-fns';
import {
  Archive,
  Loader2,
  Pencil,
  Plus,
  Search,
  Send,
  Trash2,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { AnnouncementDetails } from '~entities/announcements';
import {
  useDeleteAnnouncementMutation,
  useGetAnnouncements,
  useUpdateAnnouncementMutation,
} from '~entities/announcements';
import {
  AnnouncementStatus,
  type AnnouncementStatusType,
} from '~entities/announcements/model/types';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { useFilterPersistence } from '~shared/hooks/useFilterPersistence';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import {
  AnnouncementActionConfirmDialog,
  type AnnouncementConfirmAction,
} from './announcement-action-confirm-dialog';

const columnHelper = createColumnHelper<AnnouncementDetails>();

const ANNOUNCEMENT_SORT_KEY_MAP: Record<string, string> = {
  title: 'title',
  status: 'status',
  publishedAt: 'publishedAt',
  isPinned: 'isPinned',
  author: 'author',
};

function statusBadgeVariant(status: AnnouncementStatusType) {
  switch (status) {
    case AnnouncementStatus.PUBLISHED:
      return 'default' as const;
    case AnnouncementStatus.DRAFT:
      return 'secondary' as const;
    case AnnouncementStatus.ARCHIVED:
      return 'outline' as const;
  }
}

function formatStatus(status: AnnouncementStatusType) {
  switch (status) {
    case AnnouncementStatus.PUBLISHED:
      return 'Live';
    case AnnouncementStatus.DRAFT:
      return 'Draft';
    case AnnouncementStatus.ARCHIVED:
      return 'Archived';
  }
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  const date = parseISO(iso);
  if (!isValid(date)) return '—';
  return format(date, 'MMM dd, yyyy');
}

function stripHtmlToText(html: string) {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function AnnouncementsListTable() {
  const { mutateAsync: updateAnnouncement } = useUpdateAnnouncementMutation();
  const { mutateAsync: deleteAnnouncement } = useDeleteAnnouncementMutation();
  const { filters, pagination, updateFilter, updatePagination } =
    useFilterPersistence({
      query: '',
    });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] =
    useState<AnnouncementConfirmAction | null>(null);

  const primarySort = sorting[0];
  const sortBy = primarySort
    ? (ANNOUNCEMENT_SORT_KEY_MAP[primarySort.id] ?? primarySort.id)
    : undefined;
  const sortOrder = sortBy ? (primarySort?.desc ? 'desc' : 'asc') : undefined;

  const { data: apiData, isFetching } = useGetAnnouncements({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: filters.query,
    sortBy,
    sortOrder,
  });

  const handlePublish = useCallback(
    async (announcement: AnnouncementDetails) => {
      if (!announcement.title.trim()) {
        toast.error('Title is required');
        return;
      }
      if (!stripHtmlToText(announcement.bodyHtml)) {
        toast.error('Body is required when publishing');
        return;
      }

      setUpdatingId(announcement.id);
      try {
        await updateAnnouncement({
          id: announcement.id,
          payload: { status: AnnouncementStatus.PUBLISHED },
        });
        toast.success('Announcement published');
      } catch {
        // Error toast handled in mutation
      } finally {
        setUpdatingId(null);
      }
    },
    [updateAnnouncement],
  );

  const handleArchive = useCallback(
    async (announcement: AnnouncementDetails) => {
      setUpdatingId(announcement.id);
      try {
        await updateAnnouncement({
          id: announcement.id,
          payload: { status: AnnouncementStatus.ARCHIVED },
        });
        toast.success('Announcement archived');
      } catch {
        // Error toast handled in mutation
      } finally {
        setUpdatingId(null);
      }
    },
    [updateAnnouncement],
  );

  const handleDelete = useCallback(
    async (announcement: AnnouncementDetails) => {
      setUpdatingId(announcement.id);
      try {
        await deleteAnnouncement(announcement.id);
        toast.success('Announcement deleted');
      } catch {
        // Error toast handled in mutation
      } finally {
        setUpdatingId(null);
      }
    },
    [deleteAnnouncement],
  );

  const requestPublish = useCallback((announcement: AnnouncementDetails) => {
    if (!announcement.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!stripHtmlToText(announcement.bodyHtml)) {
      toast.error('Body is required when publishing');
      return;
    }
    setConfirmAction({ type: 'publish', announcement });
  }, []);

  const requestArchive = useCallback((announcement: AnnouncementDetails) => {
    setConfirmAction({ type: 'archive', announcement });
  }, []);

  const requestDelete = useCallback((announcement: AnnouncementDetails) => {
    setConfirmAction({ type: 'delete', announcement });
  }, []);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction) return;

    const { type, announcement } = confirmAction;

    if (type === 'publish') {
      await handlePublish(announcement);
    } else if (type === 'archive') {
      await handleArchive(announcement);
    } else {
      await handleDelete(announcement);
    }

    setConfirmAction(null);
  }, [confirmAction, handleArchive, handleDelete, handlePublish]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Title'
          />
        ),
        cell: (info) => (
          <Button
            variant='link'
            className='h-auto px-4 text-custom-secondary-1'
            asChild
          >
            <Link
              to='/announcements/$announcementId'
              params={{ announcementId: info.row.original.id }}
            >
              {info.getValue()}
            </Link>
          </Button>
        ),
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Status'
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            <Badge variant={statusBadgeVariant(info.getValue())}>
              {formatStatus(info.getValue())}
            </Badge>
          </div>
        ),
      }),
      columnHelper.accessor('publishedAt', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Published'
          />
        ),
        cell: (info) => (
          <div className='px-4'>{formatDate(info.getValue())}</div>
        ),
      }),
      columnHelper.accessor((row) => row.createdBy.name, {
        id: 'author',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Author'
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('isPinned', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Pinned'
          />
        ),
        cell: (info) => (
          <div className='px-4'>{info.getValue() ? 'Yes' : 'No'}</div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        enableSorting: false,
        header: () => (
          <div className='pl-4 font-semibold text-black'>Actions</div>
        ),
        cell: ({ row }) => {
          const announcement = row.original;
          const isUpdatingRow = updatingId === announcement.id;

          if (isUpdatingRow) {
            return (
              <div className='flex items-center px-4'>
                <Loader2 className='size-4 animate-spin text-muted-foreground' />
              </div>
            );
          }

          return (
            <div className='flex items-center gap-1 px-4'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    asChild
                    className='text-blue-500 hover:text-blue-600'
                  >
                    <Link
                      to='/announcements/$announcementId'
                      params={{ announcementId: announcement.id }}
                    >
                      <Pencil />
                      <span className='sr-only'>Edit</span>
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
              {announcement.status === AnnouncementStatus.DRAFT && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='text-green-500 hover:text-green-600'
                      onClick={() => {
                        requestPublish(announcement);
                      }}
                    >
                      <Send />
                      <span className='sr-only'>Publish</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Publish</TooltipContent>
                </Tooltip>
              )}
              {announcement.status !== AnnouncementStatus.ARCHIVED && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='text-yellow-500 hover:text-yellow-600'
                      onClick={() => {
                        requestArchive(announcement);
                      }}
                    >
                      <Archive />
                      <span className='sr-only'>Archive</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Archive</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='size-8 text-destructive hover:text-destructive'
                    onClick={() => {
                      requestDelete(announcement);
                    }}
                  >
                    <Trash2 className='size-4' />
                    <span className='sr-only'>Delete</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </div>
          );
        },
      }),
    ],
    [requestArchive, requestDelete, requestPublish, updatingId],
  );

  const handleSortingChange = useCallback(
    (updater: React.SetStateAction<SortingState>) => {
      setSorting((prev) => {
        const nextSortedState =
          typeof updater === 'function' ? updater(prev) : updater;
        updatePagination((prevPagination) => ({
          ...prevPagination,
          pageIndex: 0,
        }));
        return nextSortedState;
      });
    },
    [updatePagination],
  );

  const hasAnnouncements = (apiData?.pagination.total ?? 0) > 0;
  const isEmptyStudio =
    !isFetching && !hasAnnouncements && !filters.query.trim();

  return (
    <div className='mt-6'>
      {isEmptyStudio ? (
        <div className='rounded-lg border border-dashed py-12 text-center'>
          <p className='font-medium'>No announcements yet</p>
          <p className='mt-2 text-sm text-muted-foreground'>
            Publish updates that families will see in the Parent Portal.
          </p>
        </div>
      ) : (
        <EnrollioTable
          ariaLabel='Announcements'
          totalLabel='Announcements'
          filterHeader={
            <div className='relative w-full sm:w-[300px]'>
              <Search className='absolute left-2 top-1/2 size-4 -translate-y-1/2 text-gray-400' />
              <Input
                placeholder='Search announcements'
                type='search'
                className='w-full pl-8'
                value={filters.query}
                onChange={(e) => {
                  updateFilter('query', e.target.value);
                }}
              />
            </div>
          }
          toolbar={(table) => (
            <>
              <Button asChild className='w-full sm:w-auto'>
                <Link to='/announcements/new'>
                  <Plus />
                  New announcement
                </Link>
              </Button>
              <EnrollioTableColumnToggle table={table} />
            </>
          )}
          columns={columns}
          data={apiData?.data ?? []}
          sorting={sorting}
          isUpdating={isFetching}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          pagination={pagination}
          onPaginationChange={updatePagination}
          rowCount={apiData?.pagination.total ?? 0}
          manualPagination
          manualSorting
          getRowId={(row) => row.id}
        />
      )}

      <AnnouncementActionConfirmDialog
        confirmAction={confirmAction}
        isUpdating={updatingId !== null}
        onOpenChange={(open) => {
          if (!open && !updatingId) {
            setConfirmAction(null);
          }
        }}
        onConfirm={() => {
          void handleConfirmAction();
        }}
      />
    </div>
  );
}
